import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Button } from "@kmf/ui";
import { noticeApi, type Notice } from "@/lib/api";
import jakim from "@/assets/jakim.png";
import muis from "@/assets/muis.png";
import bpjph from "@/assets/bpjph-emblem.png";
import thailandHalal from "@/assets/thailand-halal.png";
import ifanca from "@/assets/ifanca.png";
import ara from "@/assets/ara.png";
import hccc from "@/assets/hccc.png";
import idcp from "@/assets/idcp.png";
import kfri from "@/assets/kfri.png";
import at from "@/assets/at.png";
import kotra from "@/assets/kotra.png";
import kHalal from "@/assets/k-halal.png";
import hdc from "@/assets/hdc.png";
import bsq from "@/assets/bsq.png";
import biotech from "@/assets/kogeneBiotech.png";
import smiic from "@/assets/smiic.png";
import ktr from "@/assets/ktr.png";
import qatarMoph from "@/assets/qatar-moph.svg";
import saudiShc from "@/assets/saudi-shc.svg";

const recognizingBodies = [
  { name: "말레이시아 JAKIM", logo: jakim },
  { name: "인도네시아 BPJPH", logo: bpjph, href: "/recognition/bpjph", standard: "SJPH" },
  { name: "싱가포르 MUIS", logo: muis },
  { name: "태국 CICOT", logo: thailandHalal },
  { name: "카타르 식약처", logo: qatarMoph },
  { name: "사우디 SHC (예정)", logo: saudiShc },
];

const certificationBodies = [
  { name: "미국 IFANCA", logo: ifanca },
  { name: "중국 ARA", logo: ara },
  { name: "칠레 H.C.C.C.", logo: hccc },
  { name: "필리핀 IDCP", logo: idcp },
];

// Major partner organizations (주요협력기관)
const partnerOrganizations = [
  { name: "KTR", abbr: "KTR", logo: ktr },
  { name: "한국식품연구원", abbr: "KFRI", logo: kfri },
  { name: "한국농수산식품유통공사", abbr: "aT", logo: at },
  { name: "KOTRA", abbr: "KOTRA", logo: kotra },
  { name: "한국할랄산업연구원", abbr: "K-Halal", logo: kHalal },
  { name: "할랄개발공사", abbr: "HDC", logo: hdc },
  { name: "비스퀘어", abbr: "BSQ", logo: bsq },
  { name: "한국바이오협회", abbr: "Biotech", logo: biotech },
  { name: "SMIIC", abbr: "SMIIC", logo: smiic },
];

import kmfLogo from "@/assets/kmf-logo.svg";

export function HomePage() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [noticeStatus, setNoticeStatus] = useState("loading");
  useEffect(() => {
    document.title = "KMF 할랄위원회";
    let active = true;
    noticeApi.list().then((data) => {
      if (active) { setNotices(data.slice(0, 4)); setNoticeStatus("ready"); }
    }).catch(() => { if (active) setNoticeStatus("error"); });
    return () => { active = false; };
  }, []);

  return (
    <div className="public-home">
      <section className="home-intro">
        <Container>
          <p className="eyebrow">(재)한국이슬람교 · KOREA MUSLIM FEDERATION</p>
          <div className="home-intro-grid">
            <div className="home-intro-copy">
              <h1>KMF 할랄위원회</h1>
              <p className="home-lead">할랄 인증 심사와 발급</p>
              <p className="home-description">(재)한국이슬람교 KMF 할랄위원회는 제품과 생산시설의 할랄 적합성을 심사하고 인증서를 발급합니다.</p>
              <p className="home-description">1994년부터 이어온 할랄 인증.<br />인증 신청부터 절차와 구비서류, 인증 업체 확인까지 안내합니다.</p>
              <div className="home-actions">
                <Button asChild size="lg"><Link to="/cert">인증 절차 안내 <span aria-hidden="true">↗</span></Link></Button>
                <Link className="text-link" to="/about">위원회 소개 <span aria-hidden="true">→</span></Link>
              </div>
            </div>
            <div className="home-identity">
              <p className="identity-caption">KMF HALAL CERTIFICATION</p>
              <img src={kmfLogo} alt="KMF Halal" />
              <span className="identity-footnote">KOREA MUSLIM FEDERATION</span>
            </div>
          </div>
        </Container>
      </section>
      <section className="home-services" aria-label="인증 업무 바로가기">
        <Container>
          <div className="service-links">
            {[
              { no: "01", title: "인증 신청 · 구비서류", description: "절차, 비용과 신청 서류를 확인하세요.", href: "/cert#required-documents" },
              { no: "02", title: "할랄 인증 업체", description: "KMF 할랄 인증 업체 목록을 확인하세요.", href: "/cert#halalcertifiedcompany" },
              { no: "03", title: "마크 · 인증서 사용 규칙", description: "인증 후 표시와 사용 기준을 안내합니다.", href: "/cert#mark-usage-rules" },
            ].map((item) => (
              <Link key={item.no} to={item.href} className="service-link">
                <span className="service-number">{item.no}</span>
                <h2>{item.title}<span aria-hidden="true">↗</span></h2>
                <p>{item.description}</p>
              </Link>
            ))}
          </div>
        </Container>
      </section>
      <section className="home-notices public-section">
        <Container>
          <div className="notice-layout">
            <div className="section-heading">
              <p className="eyebrow">KMF NEWSROOM</p>
              <h2>공지사항</h2>
              <p>인증 업무와 위원회의<br />새로운 소식을 전합니다.</p>
              <Link to="/notices" className="text-link">전체 보기 <span aria-hidden="true">→</span></Link>
            </div>
            <div className="notice-rows">
              {notices.length ? notices.map((notice) => (
                <Link key={notice.NoticeID} to={`/notices/${notice.NoticeID}`} className="notice-row">
                  <h3>{notice.Title}</h3>
                  <time>{notice.UpdateDateTime}</time>
                  <span aria-hidden="true">↗</span>
                </Link>
              )) : <p className="notice-empty" role="status">{noticeStatus === "loading" ? "공지사항을 불러오는 중입니다." : noticeStatus === "error" ? "공지사항을 불러오지 못했습니다." : "등록된 공지사항이 없습니다."}</p>}
            </div>
          </div>
        </Container>
      </section>
      <section className="home-recognition public-section">
        <Container>
          <div className="section-heading section-heading-inline">
            <div><p className="eyebrow">INTERNATIONAL NETWORK</p><h2>인정기관</h2></div>
            <p>세계 주요 할랄 인증기관과 함께합니다.</p>
          </div>
          <div className="logo-grid recognition-logo-grid recognition-authorities">
            {recognizingBodies.map((body) => {
              const content = <><img src={body.logo} alt={body.name} loading="lazy" /><p>{body.name}</p>{body.standard && <p className="logo-standard">인증 기준: {body.standard} <span aria-hidden="true">→</span></p>}</>;
              return body.href
                ? <Link key={body.name} to={body.href} className="logo-item logo-item-link">{content}</Link>
                : <div key={body.name} className="logo-item">{content}</div>;
            })}
          </div>
          <div className="partner-heading"><h2 className="text-xl font-medium">상호인정 해외인증기관</h2></div>
          <div className="logo-grid recognition-logo-grid">
            {certificationBodies.map((body) => <div key={body.name} className="logo-item"><img src={body.logo} alt={body.name} loading="lazy" /><p>{body.name}</p></div>)}
          </div>
          <div className="partner-heading"><h3>주요 협력기관</h3></div>
          <div className="logo-grid logo-grid-partners">
            {partnerOrganizations.map((org) => <div key={org.abbr} className="logo-item"><img src={org.logo} alt={org.name} loading="lazy" /><p>{org.name}</p></div>)}
          </div>
        </Container>
      </section>
      <section className="home-contact">
        <Container>
          <div className="contact-content">
            <div><h2>할랄 인증 신청</h2><p>구비서류를 준비한 후 통합정보관리시스템에서 신청해 주세요.</p><Link className="text-link" to="/guide">블록체인 인증 서비스 안내 <span aria-hidden="true">→</span></Link></div>
            <Button asChild size="lg"><a href="http://halal.koreaislam.org/KMFHALAL/Login" target="_blank" rel="noopener noreferrer">통합정보관리시스템 <span aria-hidden="true">↗</span></a></Button>
          </div>
        </Container>
      </section>
    </div>
  );
}
